import { useRootStore } from './index';
import { action, computed, observable } from 'mobx';
import {
  PublisherSettingsQuery_publisher,
  PublisherSettingsQuery_publisher_facebookPages,
  UpdatePublisherInput
} from 'api';
import { InstagramAccountsQuery_instagramAccounts } from '../api/kms/facebookPages/__generated__/InstagramAccountsQuery';

export class EditPublisherStore {
  @observable attributes: PublisherSettingsQuery_publisher = {
    id: 0,
    name: '',
    email: '',
    crmAccountId: '',
    temporaryHandoff: false,
    accountManager: null,
    accountSupervisor: null,
    temporaryAccountManager: null,
    active: false,
    contentEnhancerEnabled: false,
    enableAiImagesGeneration: false,
    dashboardAccessRestricted: false,
    enablePauseArticlesInKss: false,
    showStoryUrlAndSpendStatusInKss: false,
    pageLockedArticlesEnabled: false,
    billingCenter: null,
    customUserAgent: '',
    facebookPixelId: null,
    publisherFacebookAccounts: null,
    googleAccount: null,
    lastSpentDate: null,
    createdAt: '',
    selfServiceSettings: null,
    facebookPages: null,
    ssoEnabled: false,
    ssoConfiguration: null
  };

  @action setAttributes = (newAttributes: PublisherSettingsQuery_publisher) => (this.attributes = newAttributes);

  @observable selectedFacebookPages: PublisherSettingsQuery_publisher_facebookPages[] = [
    {
      instagramAccountType: null,
      instagramAccountId: null,
      facebookPage: null
    }
  ];
  @action setSelectedFacebookPages = (facebookPages: PublisherSettingsQuery_publisher_facebookPages[]) =>
    (this.selectedFacebookPages = facebookPages);

  @observable instagramAccounts: InstagramAccountsQuery_instagramAccounts[] = [
    {
      facebookPageId: '',
      accounts: null,
      errors: null
    }
  ];

  @action setInstagramAccounts = (instagramAccounts: InstagramAccountsQuery_instagramAccounts[]) =>
    (this.instagramAccounts = instagramAccounts);
  @computed get priorities(): number[] {
    if (!this.attributes.publisherFacebookAccounts) return [];

    return this.attributes.publisherFacebookAccounts.map((account) => account.priority);
  }

  @computed get publisherInput(): UpdatePublisherInput {
    return {
      id: this.attributes.id,
      email: this.attributes.email,

      temporaryHandoff: this.attributes.temporaryHandoff,
      accountManager: this.attributes.accountManager,
      accountSupervisor: this.attributes.accountSupervisor,
      temporaryAccountManager: this.attributes.temporaryAccountManager,

      active: this.attributes.active,
      contentEnhancerEnabled: !!this.attributes.contentEnhancerEnabled,
      enableAiImagesGeneration: !!this.attributes.enableAiImagesGeneration,
      enablePauseArticlesInKss: !!this.attributes.enablePauseArticlesInKss,
      showStoryUrlAndSpendStatusInKss: !!this.attributes.showStoryUrlAndSpendStatusInKss,
      pageLockedArticlesEnabled: !!this.attributes.pageLockedArticlesEnabled,
      dashboardAccessRestricted: !!this.attributes.dashboardAccessRestricted,

      billingCenter: this.attributes.billingCenter,
      selfServiceSettings: this.attributes.selfServiceSettings,
      facebookPages: this.attributes.facebookPages,
      customUserAgent: this.attributes.customUserAgent,
      facebookPixelId: this.attributes.facebookPixelId,

      publisherFacebookAccounts: this.attributes.publisherFacebookAccounts,
      googleAccount: this.attributes.googleAccount
        ? {
            googleAccountId: this.attributes.googleAccount.googleAccountId || '',
            googleAccountName: this.attributes.googleAccount.accountName || '',
            timeZone: this.attributes.googleAccount.timeZone || ''
          }
        : null,

      ssoEnabled: !!this.attributes.ssoEnabled,
      ssoConfiguration: this.attributes.ssoConfiguration
        ? {
            idpEntityId: this.attributes.ssoConfiguration.idpEntityId,
            idpSsoUrl: this.attributes.ssoConfiguration.idpSsoUrl,
            idpCertificate: this.attributes.ssoConfiguration.idpCertificate
          }
        : null
    };
  }
}

export const useEditPublisherStore = (): EditPublisherStore => {
  const store = useRootStore();
  if (!store.editPublisherStore) throw new Error('Publisher store has not been initialized');

  return store.editPublisherStore;
};
